<template>
    <div class="life-cycle-main-container">
        <h1>{{ $t("monibox.optionsMenu.lifeCycle.title") }}</h1>
        <div class="options-container">
            <div class="options-wrapper">
                <BasicDatePicker
                    v-model="dateFilter"
                    class="date-picker"
                    :yearsOptions="yearsOptions"
                    @changeDate="assignDate($event)"
                />
                <BasicDropdown
                    v-model="status"
                    name="moniboxStatus"
                    :neededPlaceholder="true"
                    :options="statusOptions"
                    @update:modelValue="updateStatus($event)"
                    class="dropdown"
                />
            </div>
            <div class="filter-button">
                <BasicButton color="light-grey" @click="showFilter">
                    <i class="fa-regular fa-filter"></i>
                </BasicButton>
            </div>
        </div>
        <div class="table-data-container">
            <div class="table-data-wrapper" v-if="!loading">
                <div class="table-data">
                    <table>
                        <thead>
                            <tr>
                                <th>{{ $t("monibox.table.dateAndHour") }}</th>
                                <th>{{ $t("vigik.global.status") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in data" :key="item.id">
                                <td>{{ item.date }}</td>
                                <td>{{ item.state }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-else-if="loading" class="loading-wrapper">
                <Loader />
            </div>
            <div class="pagination-wrapper">
                <Pagination
                    :page="page"
                    :pagesCount="pages"
                    @new-current-page="changePage"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { getLifeCycle } from "@/services/intratone/monibox"

import BasicButton from "@/components/basic/BasicButton.vue"
import BasicDropdown from "@/components/basic/BasicDropdown.vue"
import BasicDatePicker from "@/components/basic/BasicDatePicker.vue"
import Pagination from "@/components/basic/Pagination.vue"
import Loader from "@/components/basic/Loader.vue"

export default {
    components: {
        BasicButton,
        BasicDropdown,
        BasicDatePicker,
        Pagination,
        Loader,
    },

    emits: ["loading"],

    data() {
        return {
            data: [],
            loading: false,
            status: {
                name: this.$t("vigik.global.status"),
                value: 0,
            },
            statusOptions: [
                {
                    name: this.$t("vigik.global.status"),
                    value: 0,
                },
                {
                    name: this.$t(
                        "monibox.optionsMenu.lifeCycle.status.available"
                    ),
                    value: "OPERATIONAL",
                },
                {
                    name: this.$t(
                        "monibox.optionsMenu.lifeCycle.status.unavailable"
                    ),
                    value: "OUT_OF_ORDER",
                },
            ],
            moniboxId: this.$route.params.id,
            dateFilter: null,
            yearsOptions: [],
            page: 1,
            pages: 1,
        }
    },

    methods: {
        updateStatus(item) {
            this.status = item
            this.page = 1

            this.getEvents(this.status.value, this.dateFilter, this.page)
        },

        assignDate(date) {
            this.dateFilter = date
            this.page = 1

            this.getEvents(this.status.value, this.dateFilter, this.page)
        },

        async getEvents(status, date, page) {
            this.$emit("loading", true)

            let params = {
                page: page,
                limit: 8,
            }

            if (date) {
                params.startDate = date
                params.endDate = this.getTommorowDate(date)
            }

            if (status) {
                params.status = status
            }

            await getLifeCycle(this.moniboxId, params).then((response) => {
                this.data = response.data.list
                this.page = parseInt(response.data.page)
                this.pages = response.data.pages

                // Formate received Data
                this.data.forEach((element) => {
                    element.date = this.formatDate(element.date)
                    switch (element.state) {
                        case "OUT_OF_ORDER":
                            element.state = this.$t(
                                "monibox.optionsMenu.lifeCycle.status.unavailable"
                            )
                            break

                        case "OPERATIONAL":
                            element.state = this.$t(
                                "monibox.optionsMenu.lifeCycle.status.available"
                            )
                            break

                        default:
                            element.state = "-"
                    }
                })
            })

            this.$emit("loading", false)
        },

        async changePage(value) {
            this.page = value
            this.getEvents(this.status.value, this.dateFilter, this.page)
        },

        formatDate(value) {
            const date = value.split("T")[0]
            const hours = new Date(value)

            const days = date.split("-")[2]
            const month = date.split("-")[1]
            const year = date.split("-")[0]

            return `${days}/${month}/${year} ${
                hours.toTimeString().split(" ")[0]
            }`
        },

        getTommorowDate(value) {
            if (value) {
                const dayOne = new Date(value)
                dayOne.setDate(dayOne.getDate() + 1)

                return dayOne.toISOString().split("T")[0]
            } else {
                return null
            }
        },

        showFilter() {
            this.openModal(
                "monibox-filters",
                {
                    statusOptions: this.statusOptions,
                    applyFunction: (status, date) => {
                        this.getEvents(status, date, this.page)
                    },
                },
                null,
                "left-side"
            )
        },

        getYearsOptions() {
            const today = new Date()
            const year = parseInt(today.toISOString().slice(0, 4))

            for (let i = year; i > year - 5; i--) {
                this.yearsOptions.push({
                    name: i.toString(),
                    value: i,
                })
            }
        },
    },

    created() {
        this.getYearsOptions()
    },

    async mounted() {
        if (this.$route.query.status) {
            this.status = this.statusOptions.find(
                (item) => item.value === this.$route.query.status,
            )
        }

        this.dateFilter = this.$route.query.date

        await this.getEvents(this.status.value, this.dateFilter, this.page)
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global.scss";

.life-cycle-main-container {
    display: flex;
    flex-direction: column;
    gap: 1em;
    padding-top: 1em;
    height: 100%;

    h1,
    p {
        margin: 0;
    }

    h1 {
        font-family: $font-avenir-black;
    }

    .options-container {
        display: flex;
        justify-content: space-between;

        .options-wrapper {
            display: flex;
            align-items: center;
            gap: 1em;

            @media all and (max-width: 768px) {
                display: none;
            }

            .dropdown,
            .date-picker {
                min-width: 150px;
            }
        }

        .filter-button {
            @media all and (min-width: 768px) {
                display: none;
            }
        }

        .portaconnect-button {
            display: flex;
            gap: 0.5em;
            align-items: center;
        }
    }

    .table-data-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        min-height: 0;
        gap: 20px;
        flex-grow: 1;

        .table-data-wrapper {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            flex-grow: 1;
            overflow: auto;
            display: flex;

            @media all and (max-width: 768px) {
                overflow-x: scroll;
            }

            .table-data {
                flex: 1 1 auto;
                min-width: 100%;
                min-height: 0;
                table {
                    width: 100%;

                    thead,
                    tbody {
                        tr {
                            border-bottom: 1px solid $grey-silver;

                            th,
                            td {
                                padding: 1em 1em 1em 0;
                            }

                            td:nth-child(1) {
                                width: 20%;
                                min-width: 400px;
                            }
                            td:nth-child(2) {
                                width: 80%;
                            }
                        }
                    }

                    tbody {
                        tr {
                            &:hover {
                                background-color: $grey-light;
                            }
                        }
                    }
                }
            }
        }

        .loading-wrapper {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .pagination-wrapper {
            display: flex;
            align-items: end;
            flex: 0 0 auto;
            padding-bottom: 0px;
        }
    }
}
</style>
